/**
 * Workspace geographical concentration for Analytics.
 * Scanbrix often stores `country: "USA"` incorrectly — infer country from city/address text instead.
 */

import { formatWorkspaceAddress } from "./workspaceAddress";

function trimStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

const US_COUNTRY_VARIANTS = new Set([
  "usa",
  "us",
  "u.s.",
  "u.s.a.",
  "united states",
  "united states of america",
]);

/** Country names / aliases found in address text (lowercase key → display name). */
const COUNTRY_TEXT_ALIASES = {
  suomi: "Finland",
  finland: "Finland",
  germany: "Germany",
  deutschland: "Germany",
  switzerland: "Switzerland",
  schweiz: "Switzerland",
  suisse: "Switzerland",
  svizzera: "Switzerland",
  thailand: "Thailand",
  uae: "United Arab Emirates",
  "united arab emirates": "United Arab Emirates",
  emirates: "United Arab Emirates",
  egypt: "Egypt",
  india: "India",
  bharat: "India",
  france: "France",
  uk: "United Kingdom",
  "united kingdom": "United Kingdom",
  england: "United Kingdom",
  scotland: "United Kingdom",
  wales: "United Kingdom",
  canada: "Canada",
  australia: "Australia",
  mexico: "México",
  méxico: "México",
  spain: "Spain",
  españa: "Spain",
  italy: "Italy",
  italia: "Italy",
  netherlands: "Netherlands",
  holland: "Netherlands",
  belgium: "Belgium",
  austria: "Austria",
  sweden: "Sweden",
  norway: "Norway",
  denmark: "Denmark",
  poland: "Poland",
  portugal: "Portugal",
  brazil: "Brazil",
  brasil: "Brazil",
  japan: "Japan",
  china: "China",
  "south korea": "South Korea",
  korea: "South Korea",
  singapore: "Singapore",
  "saudi arabia": "Saudi Arabia",
  qatar: "Qatar",
  israel: "Israel",
  turkey: "Turkey",
  türkiye: "Turkey",
  indonesia: "Indonesia",
  malaysia: "Malaysia",
  philippines: "Philippines",
  vietnam: "Vietnam",
  "south africa": "South Africa",
  nigeria: "Nigeria",
  kenya: "Kenya",
  argentina: "Argentina",
  chile: "Chile",
  colombia: "Colombia",
  "new zealand": "New Zealand",
  ireland: "Ireland",
  greece: "Greece",
  czechia: "Czechia",
  "czech republic": "Czechia",
  hungary: "Hungary",
  romania: "Romania",
};

/** Major cities → country when the stored country field is unreliable. */
const CITY_TO_COUNTRY = {
  bangkok: "Thailand",
  dubai: "United Arab Emirates",
  "abu dhabi": "United Arab Emirates",
  sharjah: "United Arab Emirates",
  cairo: "Egypt",
  "new cairo": "Egypt",
  giza: "Egypt",
  alexandria: "Egypt",
  berlin: "Germany",
  munich: "Germany",
  hamburg: "Germany",
  frankfurt: "Germany",
  cologne: "Germany",
  sion: "Switzerland",
  zurich: "Switzerland",
  geneva: "Switzerland",
  basel: "Switzerland",
  bern: "Switzerland",
  helsinki: "Finland",
  lehmo: "Finland",
  espoo: "Finland",
  tampere: "Finland",
  paris: "France",
  lyon: "France",
  marseille: "France",
  london: "United Kingdom",
  manchester: "United Kingdom",
  birmingham: "United Kingdom",
  mumbai: "India",
  delhi: "India",
  bangalore: "India",
  bengaluru: "India",
  hyderabad: "India",
  chennai: "India",
  kolkata: "India",
  pune: "India",
  singapore: "Singapore",
  tokyo: "Japan",
  osaka: "Japan",
  sydney: "Australia",
  melbourne: "Australia",
  toronto: "Canada",
  vancouver: "Canada",
  montreal: "Canada",
  "mexico city": "México",
  madrid: "Spain",
  barcelona: "Spain",
  rome: "Italy",
  milan: "Italy",
  amsterdam: "Netherlands",
  brussels: "Belgium",
  vienna: "Austria",
  stockholm: "Sweden",
  oslo: "Norway",
  copenhagen: "Denmark",
  warsaw: "Poland",
  lisbon: "Portugal",
  "são paulo": "Brazil",
  "sao paulo": "Brazil",
  rio: "Brazil",
  beijing: "China",
  shanghai: "China",
  seoul: "South Korea",
  "hong kong": "China",
  istanbul: "Turkey",
  riyadh: "Saudi Arabia",
  doha: "Qatar",
  tel: "Israel",
  "tel aviv": "Israel",
  jakarta: "Indonesia",
  "kuala lumpur": "Malaysia",
  manila: "Philippines",
  "ho chi minh": "Vietnam",
  hanoi: "Vietnam",
  johannesburg: "South Africa",
  cape: "South Africa",
  "cape town": "South Africa",
  lagos: "Nigeria",
  nairobi: "Kenya",
  buenos: "Argentina",
  "buenos aires": "Argentina",
  santiago: "Chile",
  bogota: "Colombia",
  auckland: "New Zealand",
  dublin: "Ireland",
  athens: "Greece",
  prague: "Czechia",
  budapest: "Hungary",
  bucharest: "Romania",
  "new york": "United States",
  brooklyn: "United States",
  chicago: "United States",
  houston: "United States",
  phoenix: "United States",
  philadelphia: "United States",
  "san antonio": "United States",
  "san diego": "United States",
  dallas: "United States",
  "san jose": "United States",
  austin: "United States",
  "los angeles": "United States",
  seattle: "United States",
  denver: "United States",
  boston: "United States",
  miami: "United States",
  atlanta: "United States",
  detroit: "United States",
};

/** US cities → state (when country is genuinely United States). */
const US_CITY_TO_STATE = {
  "new york": "New York",
  brooklyn: "New York",
  chicago: "Illinois",
  houston: "Texas",
  phoenix: "Arizona",
  philadelphia: "Pennsylvania",
  "san antonio": "Texas",
  "san diego": "California",
  dallas: "Texas",
  "san jose": "California",
  austin: "Texas",
  "los angeles": "California",
  seattle: "Washington",
  denver: "Colorado",
  boston: "Massachusetts",
  miami: "Florida",
  atlanta: "Georgia",
  detroit: "Michigan",
};

function normKey(s) {
  return trimStr(s).toLowerCase().replace(/\s+/g, " ");
}

function isBogusUsCountry(country) {
  return US_COUNTRY_VARIANTS.has(normKey(country));
}

function titleCaseWords(s) {
  return trimStr(s)
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function containsPhrase(haystack, phrase) {
  const h = normKey(haystack);
  const p = normKey(phrase);
  if (!h || !p) return false;
  const re = new RegExp(`\\b${p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
  return re.test(h);
}

function inferCountryFromText(text) {
  const blob = normKey(text);
  if (!blob) return null;
  const entries = Object.entries(COUNTRY_TEXT_ALIASES).sort((a, b) => b[0].length - a[0].length);
  for (const [alias, country] of entries) {
    if (containsPhrase(blob, alias)) return country;
  }
  return null;
}

function inferCountryFromCity(city) {
  const key = normKey(city);
  if (!key) return null;
  if (CITY_TO_COUNTRY[key]) return CITY_TO_COUNTRY[key];
  for (const [cityKey, country] of Object.entries(CITY_TO_COUNTRY)) {
    if (key.includes(cityKey) || cityKey.includes(key)) return country;
  }
  return null;
}

function normalizeStoredCountry(countryRaw) {
  const c = trimStr(countryRaw);
  if (!c) return null;
  const key = normKey(c);
  if (COUNTRY_TEXT_ALIASES[key]) return COUNTRY_TEXT_ALIASES[key];
  if (isBogusUsCountry(c)) return null;
  return titleCaseWords(c);
}

/** @returns {{ street: string, city: string, pincode: string, countryRaw: string, fullAddress: string }} */
export function parseWorkspaceLocationParts(doc) {
  if (!doc || typeof doc !== "object") {
    return { street: "", city: "", pincode: "", countryRaw: "", fullAddress: "" };
  }

  const street = trimStr(doc.street ?? doc.street_address ?? doc.streetAddress);
  const cityField = trimStr(doc.city ?? doc.state);
  const pincode = trimStr(doc.zip_code ?? doc.zipCode ?? doc.postal_code ?? doc.postalCode);
  const countryRaw = trimStr(doc.country);
  const fullAddress = formatWorkspaceAddress(doc);

  let city = cityField;
  let regionHint = "";

  if (cityField.includes(",")) {
    const parts = cityField.split(",").map((p) => trimStr(p)).filter(Boolean);
    if (parts.length >= 2) {
      regionHint = parts[0];
      city = parts[parts.length - 1];
    }
  }

  if (!city && regionHint) city = regionHint;

  return { street, city, pincode, countryRaw, fullAddress, regionHint };
}

function isCountryLikeToken(token) {
  const key = normKey(token);
  if (!key) return false;
  if (COUNTRY_TEXT_ALIASES[key]) return true;
  if (isBogusUsCountry(token)) return true;
  return false;
}

export function inferWorkspaceCountry(parts) {
  const { street, city, countryRaw, fullAddress, regionHint } = parts;
  const searchText = [street, city, regionHint, fullAddress].filter(Boolean).join(" ");

  const fromText = inferCountryFromText(searchText);
  if (fromText) return fromText;

  const fromCity = inferCountryFromCity(city) || inferCountryFromCity(regionHint) || inferCountryFromCity(street);
  if (fromCity) return fromCity;

  const fromField = normalizeStoredCountry(countryRaw);
  if (fromField) return fromField;

  const cityKey = normKey(city);
  if (cityKey && US_CITY_TO_STATE[cityKey]) return "United States";

  if (isBogusUsCountry(countryRaw)) return "Unknown";

  return countryRaw ? titleCaseWords(countryRaw) : "Unknown";
}

export function inferWorkspaceState(parts, country) {
  const explicit = trimStr(
    parts.doc?.province ??
      parts.doc?.region ??
      parts.doc?.admin_area ??
      parts.doc?.adminArea ??
      parts.doc?.state_province ??
      parts.doc?.stateProvince
  );

  if (explicit && normKey(explicit) !== normKey(parts.city)) return titleCaseWords(explicit);

  const regionHint = trimStr(parts.regionHint);
  if (regionHint && !isCountryLikeToken(regionHint) && normKey(regionHint) !== normKey(parts.city)) {
    return titleCaseWords(regionHint);
  }

  const street = trimStr(parts.street);
  if (street && !isCountryLikeToken(street) && normKey(street) !== normKey(parts.city)) {
    return titleCaseWords(street);
  }

  if (country === "United States") {
    const cityKey = normKey(parts.city);
    if (US_CITY_TO_STATE[cityKey]) return US_CITY_TO_STATE[cityKey];
  }

  return "Not specified";
}

function workspaceTitle(doc) {
  return trimStr(doc.name || doc.title || doc.workspace_name) || String(doc.id || "").slice(0, 12);
}

/** @returns {{ id: string, name: string, address: string, country: string, state: string, city: string, pincode: string }} */
export function resolveWorkspaceGeo(doc) {
  const parts = parseWorkspaceLocationParts(doc);
  parts.doc = doc;
  const country = inferWorkspaceCountry(parts);
  const state = inferWorkspaceState(parts, country);
  const city = parts.city ? titleCaseWords(parts.city) : "Not specified";
  const pincode = parts.pincode || "Not specified";

  return {
    id: doc.id,
    name: workspaceTitle(doc),
    address: parts.fullAddress || "—",
    country,
    state,
    city,
    pincode,
  };
}

function countByKey(items, keyFn) {
  const map = new Map();
  for (const item of items) {
    const key = keyFn(item);
    const label = key || "Not specified";
    const prev = map.get(label);
    if (prev) prev.count += 1;
    else map.set(label, { label, count: 1, sample: item });
  }
  return map;
}

function rankedSegments(map, total) {
  return [...map.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .map((row) => ({
      ...row,
      pct: total > 0 ? (row.count / total) * 100 : 0,
    }));
}

export const GEO_CONCENTRATION_LEVELS = ["country", "state", "city", "pincode"];

/**
 * Aggregate workspace locations into country / state / city / pincode segments.
 * Ignores bogus `country: "USA"` when city or address implies another country.
 */
export function computeWorkspaceGeoConcentration(workspaces = []) {
  const list = Array.isArray(workspaces) ? workspaces : [];
  const geocoded = list.map(resolveWorkspaceGeo).filter((w) => w.address && w.address !== "—");

  const totalWithAddress = geocoded.length;
  const totalWorkspaces = list.length;

  const countryMap = countByKey(geocoded, (w) => w.country);
  const stateMap = countByKey(geocoded, (w) => `${w.country} · ${w.state}`);
  const cityMap = countByKey(geocoded, (w) => `${w.country} · ${w.city}`);
  const pincodeMap = countByKey(
    geocoded,
    (w) => `${w.country} · ${w.city} · ${w.pincode}`
  );

  const countries = rankedSegments(countryMap, totalWithAddress);
  const states = rankedSegments(stateMap, totalWithAddress);
  const cities = rankedSegments(cityMap, totalWithAddress);
  const pincodes = rankedSegments(pincodeMap, totalWithAddress);

  return {
    totalWorkspaces,
    totalWithAddress,
    topCountry: countries[0] ?? null,
    levels: {
      country: countries,
      state: states,
      city: cities,
      pincode: pincodes,
    },
    workspaces: geocoded,
  };
}

export function geoConcentrationDrillContent(level = "country", workspaces = []) {
  const geo = computeWorkspaceGeoConcentration(workspaces);
  const safeLevel = GEO_CONCENTRATION_LEVELS.includes(level) ? level : "country";
  const segments = geo.levels[safeLevel] ?? [];

  const levelLabels = {
    country: "Country",
    state: "State / region",
    city: "City",
    pincode: "Pincode",
  };

  let headers;
  let rows;

  if (safeLevel === "country") {
    headers = ["Country", "Workspaces", "Share"];
    rows = segments.map((s) => ({
      k: `geo-c-${s.label}`,
      cells: [s.label, String(s.count), `${s.pct.toFixed(1)}%`],
    }));
  } else if (safeLevel === "state") {
    headers = ["Country", "State / region", "Workspaces", "Share"];
    rows = segments.map((s) => {
      const [country, state] = s.label.split(" · ");
      return {
        k: `geo-s-${s.label}`,
        cells: [country || "—", state || "—", String(s.count), `${s.pct.toFixed(1)}%`],
      };
    });
  } else if (safeLevel === "city") {
    headers = ["Country", "City", "Workspaces", "Share"];
    rows = segments.map((s) => {
      const [country, city] = s.label.split(" · ");
      return {
        k: `geo-ci-${s.label}`,
        cells: [country || "—", city || "—", String(s.count), `${s.pct.toFixed(1)}%`],
      };
    });
  } else {
    headers = ["Country", "City", "Pincode", "Workspaces", "Share"];
    rows = segments.map((s) => {
      const [country, city, pincode] = s.label.split(" · ");
      return {
        k: `geo-p-${s.label}`,
        cells: [country || "—", city || "—", pincode || "—", String(s.count), `${s.pct.toFixed(1)}%`],
      };
    });
  }

  return {
    subtitle: `${geo.totalWithAddress} workspace(s) with an address (of ${geo.totalWorkspaces} total). Stored country "USA" is ignored when city or address text indicates another country.`,
    headers,
    rows,
    level: safeLevel,
    levelLabel: levelLabels[safeLevel],
    levels: GEO_CONCENTRATION_LEVELS,
  };
}
